import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  output,
  PLATFORM_ID,
  signal,
  untracked,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject, debounceTime } from 'rxjs';
import { CheckboxComponent } from '../../shared/ui/checkbox/checkbox.component';
import { RadioComponent } from '../../shared/ui/radio/radio.component';
import { InputComponent } from '../../shared/ui/input/input.component';
import { CATALOG_COPY, withSection, withTerm } from '../../features/catalog/catalog-copy';
import { ActiveFilters, FilterOption, FilterSection } from './filter.model';

// Umbral a partir del cual una sección de filtros se trunca con "Ver más".
// Pensado para casos como `expansion` en Pokémon o MTG, donde el bucket puede
// pasar de 30 ítems y desplaza los siguientes filtros muy abajo. El active
// filters bar arriba ya muestra los chips activos, así que el usuario puede
// quitar selecciones sin necesidad de expandir la sección.
const OPTIONS_COLLAPSE_THRESHOLD = 8;

// Umbral a partir del cual una sección checkbox/radio muestra un buscador.
// Deliberadamente IGUAL a OPTIONS_COLLAPSE_THRESHOLD pero con comparación
// INCLUSIVA (>=): con exactamente 8 opciones ya hay buscador aunque todavía no
// haya "Ver más" (que exige options.length > OPTIONS_COLLAPSE_THRESHOLD). Ambos
// umbrales conviven a propósito sin estar sincronizados.
const SEARCH_THRESHOLD = 8;

// Rango Unicode "Combining Diacritical Marks" (U+0300–U+036F), lo que deja
// `normalize('NFD')` tras separar una letra de su acento. Se construye con
// fromCharCode (no un literal \u en el código fuente) para no dejar caracteres
// combinantes invisibles pegados al propio código.
const COMBINING_MARKS = new RegExp(
  `[${String.fromCharCode(0x0300)}-${String.fromCharCode(0x036f)}]`,
  'g',
);

// El sidebar sólo se comporta como drawer (overlay fijo) por debajo de este
// ancho; debe coincidir con el @media de filter-sidebar.component.css.
const DRAWER_BREAKPOINT = '(max-width: 767px)';

@Component({
  selector: 'app-filter-sidebar',
  imports: [CheckboxComponent, RadioComponent, InputComponent],
  templateUrl: './filter-sidebar.component.html',
  styleUrl: './filter-sidebar.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(document:keydown.escape)': 'onEscape()',
  },
})
export class FilterSidebarComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly platformId = inject(PLATFORM_ID);

  sections = input.required<FilterSection[]>();
  activeFilters = input<ActiveFilters>({});
  isOpen = input<boolean>(false);

  filtersChange = output<ActiveFilters>();
  drawerClose = output<void>();

  // ¿Está el sidebar en modo drawer (móvil)? matchMedia → signal, sin CDK.
  // Gobierna el lock de scroll: en escritorio el sidebar es columna estática
  // y bloquear el scroll del body sería un bug.
  private readonly isMobile = signal(false);

  protected collapsedSections = signal<Set<string>>(new Set());
  // Set de secciones cuyo "Ver más" está abierto (ortogonal a collapsedSections,
  // que controla el acordeón completo). Por defecto vacío: todas las secciones
  // largas arrancan truncadas a OPTIONS_COLLAPSE_THRESHOLD.
  protected expandedOptionSections = signal<Set<string>>(new Set());

  // Draft local del rango por sección. Es la fuente de verdad mientras el
  // usuario tipea para que el input no parpadee con el valor viejo entre
  // pulsaciones (filtersChange se emite con debounce y activeFilters va por
  // detrás del tipeo).
  protected rangeDrafts = signal<Record<string, { min: string; max: string }>>({});

  // Término de búsqueda por sección (checkbox/radio con options.length >=
  // SEARCH_THRESHOLD). Estado puramente local de UI: no persiste en
  // URL/queryParams. Se limpia al colapsar la sección y cuando activeFilters
  // vuelve a {} (ver effect del constructor).
  protected searchTerms = signal<Record<string, string>>({});
  // Recuerda si activeFilters estaba vacío en la última corrida del effect,
  // para limpiar searchTerms solo en la TRANSICIÓN a vacío (clearAll o quitar
  // el último chip), no en cada cambio de filtros.
  private prevFiltersEmpty = true;

  private readonly rangeEmit$ = new Subject<{ sectionId: string; min: string; max: string }>();

  constructor() {
    // Sincroniza drafts cuando activeFilters cambia desde fuera (clearAll,
    // removeFilter desde la barra de chips, navegación). untracked al escribir
    // para no reentrar el effect.
    effect(() => {
      const filters = this.activeFilters();
      untracked(() => {
        const next: Record<string, { min: string; max: string }> = {};
        for (const [key, val] of Object.entries(filters)) {
          if (typeof val === 'string' && val.includes('-')) {
            const [min, max] = val.split('-');
            next[key] = { min, max };
          }
        }
        this.rangeDrafts.set(next);

        const isEmpty = Object.keys(filters).length === 0;
        if (isEmpty && !this.prevFiltersEmpty) {
          this.searchTerms.set({});
        }
        this.prevFiltersEmpty = isEmpty;
      });
    });

    this.rangeEmit$
      .pipe(debounceTime(400), takeUntilDestroyed(this.destroyRef))
      .subscribe(({ sectionId, min, max }) => {
        if (!min && !max) {
          const { [sectionId]: _, ...rest } = this.activeFilters();
          this.filtersChange.emit(rest);
        } else {
          this.filtersChange.emit({ ...this.activeFilters(), [sectionId]: `${min}-${max}` });
        }
      });

    if (!isPlatformBrowser(this.platformId)) return;

    // matchMedia → signal para saber si estamos en modo drawer.
    afterNextRender(() => {
      const mq = window.matchMedia(DRAWER_BREAKPOINT);
      this.isMobile.set(mq.matches);
      const handler = (e: MediaQueryListEvent) => this.isMobile.set(e.matches);
      mq.addEventListener('change', handler);
      this.destroyRef.onDestroy(() => mq.removeEventListener('change', handler));
    });

    // Lock del scroll del body mientras el drawer está abierto (sólo móvil).
    effect(() => {
      const locked = this.isOpen() && this.isMobile();
      document.body.style.overflow = locked ? 'hidden' : '';
    });
    this.destroyRef.onDestroy(() => {
      document.body.style.overflow = '';
    });
  }

  /** Cierra el drawer al pulsar Escape (sólo si está abierto). */
  protected onEscape(): void {
    if (this.isOpen()) this.drawerClose.emit();
  }

  protected isSectionCollapsed(id: string): boolean {
    return this.collapsedSections().has(id);
  }

  protected toggleSection(id: string): void {
    this.collapsedSections.update(set => {
      const next = new Set(set);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
    if (this.isSectionCollapsed(id)) {
      this.searchTerms.update(({ [id]: _removed, ...rest }) => rest);
    }
  }

  protected hasOverflow(section: FilterSection): boolean {
    return (
      (section.type === 'checkbox' || section.type === 'radio') &&
      section.options.length > OPTIONS_COLLAPSE_THRESHOLD
    );
  }

  protected isOptionsExpanded(id: string): boolean {
    return this.expandedOptionSections().has(id);
  }

  protected toggleOptionsExpanded(id: string): void {
    this.expandedOptionSections.update((set) => {
      const next = new Set(set);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  }

  /** Lista de opciones a renderizar respetando el truncado "Ver más". */
  protected visibleOptions(section: FilterSection): FilterOption[] {
    if (!this.hasOverflow(section) || this.isOptionsExpanded(section.id)) {
      return section.options;
    }
    return section.options.slice(0, OPTIONS_COLLAPSE_THRESHOLD);
  }

  protected hiddenCount(section: FilterSection): number {
    return Math.max(0, section.options.length - OPTIONS_COLLAPSE_THRESHOLD);
  }

  protected hasSearch(section: FilterSection): boolean {
    return (
      (section.type === 'checkbox' || section.type === 'radio') &&
      section.options.length >= SEARCH_THRESHOLD
    );
  }

  protected searchTerm(sectionId: string): string {
    return this.searchTerms()[sectionId] ?? '';
  }

  protected hasActiveSearch(sectionId: string): boolean {
    return this.searchTerm(sectionId).trim().length > 0;
  }

  protected onSearchTermChange(sectionId: string, value: string): void {
    this.searchTerms.update(terms => ({ ...terms, [sectionId]: value }));
  }

  protected searchAriaLabel(section: FilterSection): string {
    return withSection(CATALOG_COPY.filterSearch.ariaLabel, section.title);
  }

  protected readonly searchPlaceholder = CATALOG_COPY.filterSearch.placeholder;

  /** Minúsculas + sin diacríticos, para que "genetico" encuentre "Genético". */
  private normalizeForSearch(value: string): string {
    return value.toLowerCase().normalize('NFD').replace(COMBINING_MARKS, '');
  }

  /**
   * Opciones a renderizar: con búsqueda activa, TODAS las coincidencias (sin
   * truncar); sin búsqueda, la lógica de truncado existente (visibleOptions).
   */
  protected displayedOptions(section: FilterSection): FilterOption[] {
    if (!this.hasActiveSearch(section.id)) {
      return this.visibleOptions(section);
    }
    const term = this.normalizeForSearch(this.searchTerm(section.id).trim());
    return section.options.filter(opt => this.normalizeForSearch(opt.label).includes(term));
  }

  protected noMatches(section: FilterSection): boolean {
    return this.hasActiveSearch(section.id) && this.displayedOptions(section).length === 0;
  }

  protected noMatchesMessage(section: FilterSection): string {
    return withTerm(CATALOG_COPY.filterSearch.noResults, this.searchTerm(section.id).trim());
  }

  protected isChecked(sectionId: string, value: string): boolean {
    const current = this.activeFilters()[sectionId];
    if (Array.isArray(current)) return current.includes(value);
    return current === value;
  }

  protected getRadioValue(sectionId: string): string {
    const current = this.activeFilters()[sectionId];
    return typeof current === 'string' ? current : '';
  }

  protected onCheckboxChange(sectionId: string, optionValue: string, checked: boolean): void {
    const current = this.activeFilters()[sectionId];
    const existing: string[] = Array.isArray(current)
      ? [...current]
      : typeof current === 'string' && current
        ? [current]
        : [];
    const next = checked
      ? [...existing, optionValue]
      : existing.filter(v => v !== optionValue);

    this.filtersChange.emit({ ...this.activeFilters(), [sectionId]: next });
  }

  protected onRadioChange(sectionId: string, value: string): void {
    this.filtersChange.emit({ ...this.activeFilters(), [sectionId]: value });
  }

  protected getRangeMin(sectionId: string): string {
    return this.rangeDrafts()[sectionId]?.min ?? '';
  }

  protected getRangeMax(sectionId: string): string {
    return this.rangeDrafts()[sectionId]?.max ?? '';
  }

  protected onRangeChange(sectionId: string, position: 'min' | 'max', value: string): void {
    const current = this.rangeDrafts()[sectionId] ?? { min: '', max: '' };
    const next = { ...current, [position]: value };
    this.rangeDrafts.update(drafts => ({ ...drafts, [sectionId]: next }));
    this.rangeEmit$.next({ sectionId, min: next.min, max: next.max });
  }
}
